import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Form,
  FormActions,
  FormSection,
  GridFrame,
  GridScroll,
  LoadingState,
  PageHeader,
  StatusChip,
  Table,
  Td,
  TextField,
  Th,
  Tr,
  statusTone,
} from '@tessma/design-system';
import { Refusal, refusalText, formatDay, formatMoney, formatWhen, useAsync } from '../ui/async.js';
import type {
  BankAccount,
  BankTransaction,
  FinanceGateway,
  MatchRule,
  Reconciliation,
  ReconciliationMatch,
  RuleEffectiveness,
} from './gateway.js';

/**
 * BANKING AND RECONCILIATION.
 *
 * **THE UNMATCHED LIST IS THE WORKING VIEW** (FR-REC-004), not a filter over a
 * matched one. It is what the endpoint returns, in the order the endpoint
 * returns it.
 *
 * **A SUGGESTION IS SHOWN AS A SUGGESTION.** `confirmedAt === null` means the
 * computer thinks and nobody has decided: the row carries its confidence and
 * an Accept button, and until somebody presses it the bank line stays in the
 * working view. Nothing on this screen settles anything.
 *
 * **UNMATCHING ASKS FOR A REASON** because the server requires one. The reason
 * is stored with the row, which is marked rather than deleted.
 */

export function BankAccountsScreen({
  gateway,
  legalEntityId,
  onOpen,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId?: string | undefined;
  readonly onOpen: (bankAccountId: string) => void;
}) {
  const { state, reload } = useAsync<readonly BankAccount[]>(
    () => gateway.bankAccounts(legalEntityId),
    [gateway, legalEntityId],
  );

  if (state.status === 'loading') return <LoadingState title="Loading bank accounts…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  const header = <PageHeader description="Bank, cash, card and petty cash." />;

  if (state.value.length === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon="bank"
            title="No bank accounts"
            description="Bank, cash, card and petty-cash accounts appear here."
          />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Card padding="flush">
        <GridFrame>
          <GridScroll>
            <Table label="Bank accounts">
              <thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Kind</Th>
                  <Th>Currency</Th>
                  <Th>Account</Th>
                </Tr>
              </thead>
              <tbody>
                {state.value.map((account) => (
                  <Tr key={account.id}>
                    <Td>
                      <Button
                        variant="tertiary"
                        onClick={() => {
                          onOpen(account.id);
                        }}
                      >
                        {account.name}
                      </Button>
                    </Td>
                    <Td variant="secondary">{account.accountKind}</Td>
                    <Td variant="secondary">{account.currency}</Td>
                    {/*
                    THE LAST FOUR DIGITS ARE ALL THE SERVER HOLDS. There is no
                    column here for a full account number to come from.
                  */}
                    <Td variant="secondary">
                      {account.accountLast4 === null ? '—' : `•••• ${account.accountLast4}`}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>
    </>
  );
}

/**
 * THE TENANT'S OWN MATCHING RULES (FR-REC-002).
 *
 * **A RULE RANKS AND SUGGESTS. IT NEVER CONFIRMS**, and this screen says so
 * where somebody writing one will read it. A rule is a guess about a statement
 * narrative typed into a text box; letting one confirm would mean a pattern
 * that matched too broadly silently reconciling the wrong document, and
 * reconciliation is the control that exists to catch exactly that. There is no
 * "auto-confirm" toggle here because there is none in the server either.
 *
 * **CONFIDENCE ORDERS THE LIST, AND IS NOT A PROBABILITY.** A tenant who
 * trusts a rule gives it a high number; that buys it the top of the list and
 * nothing else.
 */
export function MatchRulesScreen({
  gateway,
  legalEntityId,
  canManage,
}: {
  readonly gateway: FinanceGateway;
  readonly legalEntityId: string;
  readonly canManage: boolean;
}) {
  const { state, reload } = useAsync<{
    rules: readonly MatchRule[];
    effectiveness: readonly RuleEffectiveness[];
  }>(
    async () => ({
      rules: await gateway.matchRules(legalEntityId),
      effectiveness: await gateway.ruleEffectiveness(legalEntityId),
    }),
    [gateway, legalEntityId],
  );

  const [name, setName] = useState('');
  const [narrative, setNarrative] = useState('');
  const [windowDays, setWindowDays] = useState('5');
  const [toleranceBp, setToleranceBp] = useState('0');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  if (state.status === 'loading') return <LoadingState title="Loading matching rules…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function save() {
    setSaving(true);
    setProblem(undefined);
    try {
      await gateway.saveMatchRule({
        legalEntityId,
        name,
        ...(narrative === '' ? {} : { narrativeContains: narrative }),
        windowDays: Number(windowDays),
        amountToleranceBp: Number(toleranceBp),
      });
      setName('');
      setNarrative('');
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The rule could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const accepted = (ruleId: string) =>
    state.value.effectiveness.find((row) => row.ruleId === ruleId);

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title="Matching rules"
          subtitle="Yours, in the order they run. A rule suggests and ranks; a person still confirms every match."
        />
        {state.value.rules.length === 0 ? (
          <EmptyState
            icon="funnel"
            title="No rules yet"
            description="Without one, matching uses the built-in heuristic: exact amount, five-day window."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Matching rules">
                <thead>
                  <Tr>
                    <Th align="right">Priority</Th>
                    <Th>Rule</Th>
                    <Th>Narrative contains</Th>
                    <Th align="right">Window</Th>
                    <Th align="right">Tolerance</Th>
                    <Th align="right">Suggested</Th>
                    <Th align="right">Accepted</Th>
                  </Tr>
                </thead>
                <tbody>
                  {state.value.rules.map((rule) => (
                    <Tr key={rule.id}>
                      <Td variant="money">{rule.priority}</Td>
                      <Td>
                        {rule.name}
                        {rule.legalEntityId === null ? ' (tenant-wide)' : ''}
                      </Td>
                      <Td variant="secondary">{rule.narrativeContains ?? 'anything'}</Td>
                      <Td variant="money">{rule.windowDays} days</Td>
                      <Td variant="money">
                        {rule.amountToleranceBp === 0
                          ? formatMoney(rule.amountTolerance, 'GBP')
                          : `${String(rule.amountToleranceBp / 100)}%`}
                      </Td>
                      <Td variant="money">{accepted(rule.id)?.suggested ?? 0}</Td>
                      <Td variant="money">{accepted(rule.id)?.confirmed ?? 0}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {canManage ? (
        <Card>
          <CardHeader
            title="Add a rule"
            subtitle="It will suggest matches. It will never confirm one."
          />
          {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
          <Alert tone="info" title="A rule cannot reconcile anything by itself">
            Every match it produces is a suggestion until a person accepts it. That is the same rule
            the AI gateway follows, and for the same reason.
          </Alert>
          <Form
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <FormSection title="Rule">
              <TextField
                label="Name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                required
              />
              <TextField
                label="Statement narrative contains"
                value={narrative}
                onChange={(event) => {
                  setNarrative(event.target.value);
                }}
                helper="A substring, not a pattern. Case does not matter. Leave blank to match any line."
              />
              <TextField
                label="Date window, in days"
                value={windowDays}
                onChange={(event) => {
                  setWindowDays(event.target.value);
                }}
                helper="How far apart the bank date and the document date may be."
              />
              <TextField
                label="Amount tolerance, in basis points"
                value={toleranceBp}
                onChange={(event) => {
                  setToleranceBp(event.target.value);
                }}
                helper="175 is 1.75% — a card processor's fee. 0 is an exact match."
              />
            </FormSection>
            <FormActions submitLabel="Save the rule" submitting={saving} />
          </Form>
        </Card>
      ) : null}
    </div>
  );
}

export function BankReconciliationScreen({
  gateway,
  bankAccountId,
  canReconcile,
  canUnreconcile,
}: {
  readonly gateway: FinanceGateway;
  readonly bankAccountId: string;
  readonly canReconcile: boolean;
  readonly canUnreconcile: boolean;
}) {
  const { state, reload } = useAsync<{
    transactions: readonly BankTransaction[];
    reconciliations: readonly Reconciliation[];
  }>(
    async () => ({
      transactions: await gateway.unmatched(bankAccountId),
      reconciliations: await gateway.reconciliations(bankAccountId),
    }),
    [gateway, bankAccountId],
  );

  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [matches, setMatches] = useState<readonly ReconciliationMatch[]>([]);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState('');

  if (state.status === 'loading') return <LoadingState title="Loading the reconciliation…" />;
  if (state.status === 'failed') return <Refusal error={state.error} onRetry={reload} />;
  if (state.status !== 'ready') return null;

  async function suggest(transactionId: string) {
    setProblem(undefined);
    setSelected(transactionId);
    try {
      const existing = await gateway.matches(transactionId);
      const produced = existing.length > 0 ? existing : await gateway.suggest(transactionId);
      setMatches(produced);
    } catch (error) {
      setProblem(refusalText(error, 'Could not suggest matches.'));
    }
  }

  async function accept(matchId: string) {
    setProblem(undefined);
    try {
      await gateway.acceptSuggestion(matchId);
      setMatches([]);
      setSelected(undefined);
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The match was refused.'));
    }
  }

  async function undo(matchId: string) {
    setProblem(undefined);
    try {
      await gateway.unmatch(matchId, reason);
      setReason('');
      setMatches([]);
      reload();
    } catch (error) {
      setProblem(refusalText(error, 'The unmatch was refused.'));
    }
  }

  return (
    <div>
      <Card padding="flush">
        <CardHeader
          title="Unmatched bank lines"
          subtitle="The working view: what is still unsettled, oldest first."
        />
        {problem !== undefined ? <Alert tone="danger" title={problem} /> : null}
        {state.value.transactions.length === 0 ? (
          <EmptyState
            icon="check-circle"
            title="Everything is matched"
            description="No bank line in this account is waiting for a decision."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Unmatched bank lines">
                <thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Description</Th>
                    <Th>Reference</Th>
                    <Th align="right">Amount</Th>
                    <Th>State</Th>
                    {canReconcile ? <Th>Action</Th> : null}
                  </Tr>
                </thead>
                <tbody>
                  {state.value.transactions.map((transaction) => (
                    <Tr key={transaction.id}>
                      <Td variant="secondary">{formatDay(transaction.transactedOn)}</Td>
                      <Td>{transaction.description}</Td>
                      <Td variant="secondary">{transaction.reference ?? '—'}</Td>
                      <Td variant="money">
                        {formatMoney(transaction.amount, transaction.currency)}
                      </Td>
                      <Td>
                        <StatusChip tone={statusTone(transaction.matchState)}>
                          {transaction.matchState.replace(/_/g, ' ')}
                        </StatusChip>
                      </Td>
                      {canReconcile ? (
                        <Td>
                          <Button
                            variant="tertiary"
                            onClick={() => {
                              void suggest(transaction.id);
                            }}
                          >
                            Suggest matches
                          </Button>
                        </Td>
                      ) : null}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>

      {selected !== undefined ? (
        <Card>
          <CardHeader
            title="Suggestions"
            subtitle="The computer thinks; a person decides. Nothing here is settled yet."
          />
          {matches.length === 0 ? (
            <EmptyState
              icon="magnifying-glass"
              title="Nothing to suggest"
              description="No document within your reach matches this line."
            />
          ) : (
            <GridFrame>
              <GridScroll>
                <Table label="Suggestions">
                  <thead>
                    <Tr>
                      <Th>Document</Th>
                      <Th align="right">Amount</Th>
                      <Th>Confidence</Th>
                      <Th>Decided</Th>
                      <Th>Action</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {matches.map((match) => (
                      <Tr key={match.id}>
                        <Td>
                          {match.targetKind} {match.targetId.slice(0, 8)}
                        </Td>
                        <Td variant="money">{formatMoney(match.amount, 'GBP')}</Td>
                        <Td variant="secondary">{match.confidence ?? '—'}</Td>
                        <Td variant="secondary">
                          {match.confirmedAt === null ? (
                            <StatusChip tone="warning">suggestion</StatusChip>
                          ) : (
                            formatWhen(match.confirmedAt)
                          )}
                        </Td>
                        <Td>
                          {match.confirmedAt === null ? (
                            canReconcile ? (
                              <Button
                                variant="primary"
                                onClick={() => {
                                  void accept(match.id);
                                }}
                              >
                                Accept
                              </Button>
                            ) : null
                          ) : canUnreconcile ? (
                            <Button
                              variant="tertiary"
                              onClick={() => {
                                void undo(match.id);
                              }}
                              disabled={reason.trim() === ''}
                            >
                              Unmatch
                            </Button>
                          ) : null}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </GridScroll>
            </GridFrame>
          )}
          {canUnreconcile ? (
            <TextField
              label="Reason for unmatching"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              helper="Required. It is stored with the row, which is marked rather than deleted."
            />
          ) : null}
        </Card>
      ) : null}

      <Card padding="flush">
        <CardHeader title="Reconciliations" subtitle="Signed off, or still in progress." />
        {state.value.reconciliations.length === 0 ? (
          <EmptyState
            icon="calendar-blank"
            title="No reconciliations"
            description="A reconciliation covers a period of this account."
          />
        ) : (
          <GridFrame>
            <GridScroll>
              <Table label="Reconciliations">
                <thead>
                  <Tr>
                    <Th>From</Th>
                    <Th>To</Th>
                    <Th align="right">Statement</Th>
                    <Th align="right">Ledger</Th>
                    <Th>Status</Th>
                  </Tr>
                </thead>
                <tbody>
                  {state.value.reconciliations.map((reconciliation) => (
                    <Tr key={reconciliation.id}>
                      <Td variant="secondary">{formatDay(reconciliation.fromDate)}</Td>
                      <Td variant="secondary">{formatDay(reconciliation.toDate)}</Td>
                      <Td variant="money">{formatMoney(reconciliation.statementBalance, 'GBP')}</Td>
                      <Td variant="money">{formatMoney(reconciliation.ledgerBalance, 'GBP')}</Td>
                      <Td>
                        <StatusChip tone={statusTone(reconciliation.status)}>
                          {reconciliation.status.replace(/_/g, ' ')}
                        </StatusChip>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </GridScroll>
          </GridFrame>
        )}
      </Card>
    </div>
  );
}
